/**
 * Unternehmensprofil › Leistungen: whom each service group of a salon is shown to.
 *
 * After "Termin buchen" the WhatsApp agent lists the salon's services, narrowed
 * to the groups for the customer's gender. TimeGlobe has no such field — the
 * agent reads it off the group name ("Damen Color"), and here the salon sees
 * that reading per salon and corrects it. Every click saves at once; the
 * preview on the right is the list a customer would get.
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { AlertCircle, Check, HelpCircle, Loader2, Scissors } from 'lucide-react';
import authService, {
  type ServiceAudience,
  type ServiceGroup,
} from '../../services/authService';
import {
  audienceFromFlags,
  distinctServices,
  isFor,
  overviewFor,
  type Viewer,
} from './serviceOverview';

interface Salon {
  siteCd: string;
  siteNm: string;
}

type Status = 'loading' | 'ready' | 'error';

const VIEWERS: { id: Viewer; label: string; noun: string }[] = [
  { id: 'female', label: 'Kundin', noun: 'Damen' },
  { id: 'male', label: 'Kunde', noun: 'Herren' },
  { id: 'unknown', label: 'Ohne Angabe', noun: '' },
];

const CHIP_ON =
  'text-sm px-3 py-1.5 rounded-full border border-[#1967D2] bg-[#1967D2] text-white transition-colors';
const CHIP_OFF =
  'text-sm px-3 py-1.5 rounded-full border border-border bg-background text-muted-foreground transition-colors hover:border-[#1967D2] hover:text-foreground';

const SECTION_LABEL = 'text-xs text-muted-foreground uppercase tracking-wider font-semibold';

const countLabel = (services: { standalone: boolean }[]): string => {
  const total = services.length;
  const extras = services.filter((s) => !s.standalone).length;
  const base = total === 1 ? '1 Leistung' : `${total} Leistungen`;
  if (extras === 0 || extras === total) return base;
  return `${base}, davon ${extras === 1 ? '1 Zusatzleistung' : `${extras} Zusatzleistungen`}`;
};

// Saves go out one after the other, in the order of the clicks, so the server
// ends on the last one. The queue outlives the component: leaving the tab and
// coming back must not load the groups ahead of a save that is still on its way.
let saveQueue: Promise<void> = Promise.resolve();

export default function ServiceGroupsSection() {
  const selectId = useId();
  const [sites, setSites] = useState<Salon[]>([]);
  const [siteCd, setSiteCd] = useState<string | null>(null);
  const [groups, setGroups] = useState<ServiceGroup[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const [pendingSaves, setPendingSaves] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [viewer, setViewer] = useState<Viewer>('female');
  // Only the latest request may fill the screen: salons can be switched
  // faster than TimeGlobe answers.
  const requestRef = useRef(0);
  const siteRef = useRef<string | null>(null);
  // Clicks are never blocked while a save runs ("Damen", then "Herren" right
  // away). `clicks` numbers them per group — only the last click on a group
  // may put the choice back when its save fails; `confirmed` is what the
  // server has.
  const clicksRef = useRef<Record<string, number>>({});
  const confirmedRef = useRef<Record<string, ServiceAudience>>({});

  const load = useCallback(async (wanted?: string) => {
    const request = ++requestRef.current;
    setStatus('loading');
    setJustSaved(false);
    try {
      // Never ahead of a save: the answer would show the state before it.
      await saveQueue;
      const data = await authService.getServiceGroups(wanted);
      if (request !== requestRef.current) return;
      siteRef.current = data.site_cd;
      clicksRef.current = {};
      confirmedRef.current = Object.fromEntries((data.groups || []).map((g) => [g.name, g.audience]));
      setSites(data.sites || []);
      setSiteCd(data.site_cd);
      setGroups(data.groups || []);
      setStatus('ready');
    } catch {
      if (request !== requestRef.current) return;
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const pickSite = (next: string) => {
    siteRef.current = next;
    setSiteCd(next);
    setGroups([]);
    void load(next);
  };

  const assign = (group: ServiceGroup, audience: ServiceAudience) => {
    const site = siteCd;
    const name = group.name;
    if (!site || audience === group.audience) return;
    const show = (value: ServiceAudience) =>
      setGroups((prev) => prev.map((g) => (g.name === name ? { ...g, audience: value } : g)));

    const click = (clicksRef.current[name] ?? 0) + 1;
    clicksRef.current = { ...clicksRef.current, [name]: click };
    show(audience);
    setJustSaved(false);
    setPendingSaves((n) => n + 1);
    saveQueue = saveQueue.then(async () => {
      // Another salon may be on screen by now — its group of the same name is not ours to touch.
      const stillHere = () => siteRef.current === site;
      try {
        await authService.saveServiceGroups(site, [{ name, audience }]);
        if (stillHere()) {
          confirmedRef.current = { ...confirmedRef.current, [name]: audience };
          setJustSaved(true);
        }
      } catch {
        // A later click on this group has its own save coming and decides how it ends.
        if (!stillHere() || clicksRef.current[name] !== click) return;
        show(confirmedRef.current[name] ?? group.default_audience);
        setJustSaved(false);
        toast.error(`„${name}“ wurde nicht gespeichert. Bitte versuche es noch einmal.`);
      } finally {
        setPendingSaves((n) => n - 1);
      }
    });
  };

  const toggle = (group: ServiceGroup, side: 'female' | 'male') => {
    const women = side === 'female' ? !isFor(group.audience, 'female') : isFor(group.audience, 'female');
    const men = side === 'male' ? !isFor(group.audience, 'male') : isFor(group.audience, 'male');
    assign(group, audienceFromFlags(women, men));
  };

  const siteName = sites.find((s) => s.siteCd === siteCd)?.siteNm || 'eurem Salon';
  // With nothing bookable on its own the agent shows everything, extras included.
  const anyStandalone = groups.some((g) => g.services.some((s) => s.standalone));
  const overview = overviewFor(groups, viewer, siteName);
  const viewerNoun = VIEWERS.find((v) => v.id === viewer)?.noun || '';

  return (
    <Card className="shadow-sm border-border">
      <CardHeader>
        <div className="flex items-start gap-3">
          <Scissors className="h-5 w-5 text-[#1967D2] mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-lg">Leistungen</CardTitle>
              <Link
                to="/help?tab=leistungen"
                className="shrink-0 text-muted-foreground hover:text-[#1967D2] transition-colors"
                title="Wie funktioniert das? Zur Anleitung"
                aria-label="Anleitung zu den Leistungen öffnen"
              >
                <HelpCircle className="h-5 w-5" />
              </Link>
            </div>
            <CardDescription>
              Tippt ein Kunde in WhatsApp auf „Termin buchen“, zeigt der Agent eine Übersicht eurer
              Leistungen. Kennt er das Geschlecht, zeigt er nur die Gruppen für Damen bzw. Herren.
              Hier legst du je Salon fest, welche Gruppe wem gezeigt wird.
              <span className="block mt-2">
                Buchbar bleibt immer alles: Nennt ein Kunde eine Leistung, findet der Agent sie —
                egal in welcher Gruppe.
              </span>
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {sites.length > 1 && (
          <div className="space-y-1.5 max-w-sm">
            <Label htmlFor={selectId}>Salon</Label>
            <select
              id={selectId}
              value={siteCd ?? ''}
              onChange={(event) => pickSite(event.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {sites.map((site) => (
                <option key={site.siteCd} value={site.siteCd}>
                  {site.siteNm}
                </option>
              ))}
            </select>
          </div>
        )}

        {status === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" role="status">
            <Loader2 className="h-4 w-4 animate-spin" />
            Leistungen werden geladen…
          </div>
        )}

        {status === 'error' && (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-4 text-sm">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
            <span className="flex-1">Die Leistungen konnten nicht aus TimeGlobe geladen werden.</span>
            <Button variant="outline" size="sm" onClick={() => void load(siteCd ?? undefined)}>
              Erneut versuchen
            </Button>
          </div>
        )}

        {status === 'ready' && sites.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Noch kein Salon aus TimeGlobe verbunden — das passiert in der Einrichtung.
          </p>
        )}

        {status === 'ready' && sites.length > 0 && groups.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Für diesen Salon sind in TimeGlobe keine online buchbaren Leistungen hinterlegt.
          </p>
        )}

        {status === 'ready' && groups.length > 0 && (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
            <div className="space-y-3">
              <div className="flex items-baseline justify-between gap-3">
                <p className={SECTION_LABEL}>Gruppen aus TimeGlobe</p>
                <span role="status" className="text-xs text-muted-foreground flex items-center gap-1 h-4">
                  {pendingSaves > 0 ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Speichert…
                    </>
                  ) : justSaved ? (
                    <>
                      <Check className="h-3 w-3 text-[#1967D2]" />
                      Gespeichert
                    </>
                  ) : null}
                </span>
              </div>

              <ul className="divide-y divide-border rounded-lg border border-border">
                {groups.map((group) => {
                  const services = distinctServices(group);
                  const extrasOnly = anyStandalone && services.every((s) => !s.standalone);
                  const changed = group.audience !== group.default_audience;
                  return (
                    <li key={group.name}>
                      <div role="group" aria-label={group.name} className="p-3 space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                          <span className="font-medium text-sm">{group.name}</span>
                          {!extrasOnly && (
                            <div className="flex items-center gap-2">
                              {(['female', 'male'] as const).map((side) => {
                                const active = isFor(group.audience, side);
                                return (
                                  <button
                                    key={side}
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => toggle(group, side)}
                                    className={active ? CHIP_ON : CHIP_OFF}
                                  >
                                    {side === 'female' ? 'Damen' : 'Herren'}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {extrasOnly && (
                          <p className="text-xs text-muted-foreground">
                            Nur Zusatzleistungen — sie stehen nicht in der Übersicht, weil sie nur
                            zusammen mit einer Hauptleistung buchbar sind. Der Agent bietet sie
                            passend zur gewählten Leistung an (
                            <Link to="/voice-agent" className="text-[#1967D2] hover:underline">
                              Cross-Selling einstellen
                            </Link>
                            ).
                          </p>
                        )}

                        {changed && !extrasOnly && (
                          <p className="text-xs text-muted-foreground">
                            Von dir geändert ·{' '}
                            <button
                              type="button"
                              aria-label={`${group.name} zurücksetzen`}
                              onClick={() => assign(group, group.default_audience)}
                              className="text-[#1967D2] hover:underline"
                            >
                              Zurücksetzen
                            </button>
                          </p>
                        )}

                        <details className="text-xs text-muted-foreground">
                          <summary className="cursor-pointer select-none hover:text-foreground">
                            {countLabel(services)}
                          </summary>
                          <ul className="mt-2 space-y-1 pl-4">
                            {services.map((service) => (
                              <li key={service.name} className="flex flex-wrap items-center gap-2">
                                <span>{service.name}</span>
                                {!service.standalone && (
                                  <span className="rounded-full border border-border px-1.5 py-px text-[10px] uppercase tracking-wide">
                                    Zusatz
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </details>
                      </div>
                    </li>
                  );
                })}
              </ul>

              <p className="text-xs text-muted-foreground">
                Ohne Auswahl steht eine Gruppe nur in der Gesamtliste. Die bekommt, wessen
                Geschlecht der Agent nicht kennt.
              </p>
            </div>

            <section aria-label="Vorschau der Liste nach „Termin buchen“" className="space-y-3">
              <p className={SECTION_LABEL}>Vorschau</p>
              <div role="group" aria-label="Vorschau für" className="flex flex-wrap gap-1.5">
                {VIEWERS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={viewer === option.id}
                    onClick={() => setViewer(option.id)}
                    className={
                      viewer === option.id
                        ? 'text-xs px-2.5 py-1 rounded-full border border-foreground bg-foreground text-background transition-colors'
                        : 'text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground transition-colors'
                    }
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="rounded-lg rounded-tl-sm border border-border bg-muted/40 p-3 text-sm space-y-2 shadow-sm">
                {overview.mode === 'question' && (
                  <p>
                    Gerne – welche Leistung möchtest du bei <strong>{siteName}</strong> buchen?
                  </p>
                )}
                {overview.mode === 'groups' && (
                  <>
                    <p>
                      Gerne – bei <strong>{siteName}</strong> gibt es Leistungen
                      {overview.narrowed ? ` (${viewerNoun})` : ''} in diesen Bereichen:
                    </p>
                    <ul className="space-y-0.5">
                      {overview.groups.map((g) => (
                        <li key={g.name} className="flex gap-1.5">
                          <span aria-hidden="true">•</span>
                          <span>{g.name}</span>
                        </li>
                      ))}
                    </ul>
                    <p>Was möchtest du gerne buchen?</p>
                  </>
                )}
                {overview.mode === 'list' && (
                  <>
                    <p>
                      Gerne – hier sind die verfügbaren Dienstleistungen
                      {overview.narrowed ? ` (${viewerNoun})` : ''} bei <strong>{siteName}</strong>:
                    </p>
                    {overview.groups.map((g) => (
                      <div key={g.name}>
                        {overview.groups.length > 1 && <p className="font-semibold">{g.name}</p>}
                        <ul className="space-y-0.5">
                          {g.services.map((name) => (
                            <li key={name} className="flex gap-1.5">
                              <span aria-hidden="true">•</span>
                              <span>{name}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <p>Welche dieser Leistungen möchtest du gerne buchen?</p>
                  </>
                )}
              </div>

              {viewer !== 'unknown' && !overview.narrowed && (
                <p className="text-xs text-muted-foreground">
                  Für {viewerNoun} ist keine Gruppe mit einzeln buchbaren Leistungen zugeteilt — der
                  Agent zeigt deshalb alle.
                </p>
              )}
              {overview.mode !== 'list' && (
                <p className="text-xs text-muted-foreground">
                  Die ganze Liste passt nicht in eine WhatsApp-Nachricht.{' '}
                  {overview.mode === 'groups'
                    ? 'Der Agent nennt dann die Bereiche und fragt nach dem Wunsch.'
                    : 'Der Agent fragt dann direkt nach dem Wunsch.'}
                </p>
              )}
            </section>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
